import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;          // 0-indexed
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

// Build a compact page list with ellipsis gaps, e.g. [0, '…', 4, 5, 6, '…', 11]
function pageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const out: (number | '…')[] = [];
  const push = (n: number | '…') => out.push(n);
  push(0);
  const start = Math.max(1, current - 1);
  const end = Math.min(total - 2, current + 1);
  if (start > 1) push('…');
  for (let i = start; i <= end; i++) push(i);
  if (end < total - 2) push('…');
  push(total - 1);
  return out;
}

export function TransactionPagination({ page, totalItems, pageSize, onPageChange }: Props) {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalItems <= pageSize) return null;

  const first = page * pageSize + 1;
  const last = Math.min(totalItems, (page + 1) * pageSize);

  const arrowClass =
    'flex items-center justify-center w-7 h-7 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div
      className="flex items-center justify-between gap-3 mt-4 pt-4 flex-wrap"
      style={{ borderTop: '0.5px solid rgb(var(--overlay) / 0.06)' }}
    >
      <span className="text-xs" style={{ color: 'var(--color-sub)' }}>
        Showing <span style={{ color: 'rgb(var(--sl-100))' }}>{first.toLocaleString()}</span>–
        <span style={{ color: 'rgb(var(--sl-100))' }}>{last.toLocaleString()}</span> of{' '}
        <span style={{ color: 'rgb(var(--sl-100))' }}>{totalItems.toLocaleString()}</span>
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          className={arrowClass}
          style={{ color: 'var(--color-sub)' }}
          title="Previous"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {pageList(page, totalPages).map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="px-1 text-xs" style={{ color: 'var(--color-sub)' }}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className="min-w-[28px] h-7 px-2 rounded-md text-xs font-medium transition-colors num"
              style={
                p === page
                  ? { backgroundColor: '#d4b483', color: '#1a1410' }
                  : { color: 'rgb(var(--sl-100))', backgroundColor: 'transparent' }
              }
              onMouseEnter={(e) => {
                if (p !== page) e.currentTarget.style.backgroundColor = 'rgb(var(--overlay) / 0.05)';
              }}
              onMouseLeave={(e) => {
                if (p !== page) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {p + 1}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className={arrowClass}
          style={{ color: 'var(--color-sub)' }}
          title="Next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
